import { useEffect, useRef, useState } from 'react';
import type { TimingData } from '@timer-manager/shared';
import { parseServerTimeOfDay } from '../utils/time.ts';
import { resolveFollowingLive, type FollowingLive } from '../utils/following.ts';

/**
 * The "server anchor": the last TimingData the server sent, together with the
 * local Date.now() at which we received it. All interpolated values are
 * computed as `server_value − elapsed_since_anchor`.
 */
export interface TimingAnchor {
  data: TimingData;
  /** Date.now() when this anchor was received. */
  receivedAt: number;
  /** Server "current_time" parsed as an absolute Date using receivedAt's date. */
  serverNowAtReceipt: Date;
}

/**
 * A snapshot of what the dashboard should currently display.
 * Recomputed ~10 times per second by the hook below.
 */
export interface InterpolatedTiming {
  /** NTP wall-clock time to display in the Clock component. */
  now: Date;
  /** item_remaining_time after local interpolation AND transmission-delay subtraction. */
  itemRemainingSeconds: number;
  /**
   * True when the current live clip's countdown has passed zero from the
   * presenter's perspective (raw remaining time is negative after delay
   * subtraction). During this window — up to `transmission_delay_ms` long —
   * `is_live` is still true at the OTAV level, but the presenter has finished
   * speaking. The dashboard hides the rendu d'antenne card and the ON AIR
   * indicator to avoid showing a frozen "00:00:00".
   */
  itemRemainingExpired: boolean;
  /** Display name of the current clip. */
  itemDisplayName: string;
  /** True if the current clip is a live (ON AIR habillage). */
  isLive: boolean;
  /** Active OTAV source (MAIN/BACKUP). */
  activeSource: 'main' | 'backup';
  /** Name of the next live, if any. */
  nextLiveDisplayName: string | undefined;
  /** Seconds until the next live, interpolated + delay-adjusted. undefined if no next live. */
  nextLiveRemainingSeconds: number | undefined;
  /** Wall-clock Date at which the next live is scheduled to start, delay-adjusted. */
  nextLiveAt: Date | undefined;
  /**
   * Whether the "prise d'antenne" card should currently be shown.
   *
   * True while a next live exists AND we are not yet more than 1 second past
   * its start moment. The extra second allows the card to display "00:00:00"
   * visibly for a full second (alongside the synthetic rendu card which has
   * already appeared) before hiding — giving a clean
   *   …3, 2, 1, 0 [hide]
   * sequence rather than "1 [hide]" (where 0 would never be perceived).
   */
  nextLiveCardVisible: boolean;

  /**
   * Synthetic "rendu d'antenne" countdown used during the transmission-delay
   * window: after the "prise d'antenne" countdown has hit 0 (presenter started
   * speaking) but before the OTAV has finished airing the previous clip and
   * flipped `is_live` to true. Computed as `nextLiveDuration + rawNext` where
   * `rawNext` is the (negative) remaining-time-until-next-live minus elapsed
   * minus delay. undefined when we're not in the window, or when the next
   * live's duration wasn't pre-fetched by the collector.
   */
  syntheticRenduSeconds: number | undefined;
  /** Name to display while `syntheticRenduSeconds` is active. */
  syntheticRenduName: string | undefined;
  /**
   * Raw duration (in whole seconds) of the next live, as pre-fetched by the
   * collector. Used by the dashboard to render the hidden 3rd card when two
   * lives chain back-to-back. undefined if the collector hasn't supplied it.
   */
  nextLiveDurationSeconds: number | undefined;
  /**
   * The live that follows the next one (N+2), resolved from the collector's
   * `following_live_*` fields. See `resolveFollowingLive` for the three
   * states; `live` always carries a valid Date, safe for `formatClock`.
   */
  followingLive: FollowingLive;
}

/** How often we recompute the interpolated snapshot (ms). 100 ms → 10 FPS. */
const INTERP_TICK_MS = 100;

/**
 * Hysteresis threshold for the stabilised end-times: we only reset a locked
 * endMs when the new candidate drifts more than this much from the stored
 * value. Typical OTAV jitter is a few ms; a 500 ms threshold absorbs all of
 * that noise while remaining reactive to real corrections.
 */
const END_MS_HYSTERESIS = 500;

/**
 * Hysteresis threshold for the NTP-offset (server clock − local clock). We
 * only update the stable offset when a new anchor's candidate drifts more
 * than this much from the stored value. Network round-trip jitter on each
 * `timing_update` typically perturbs the offset by a few tens of ms; without
 * this filter, the displayed wall-clock recomputed from `Date.now() + offset`
 * can step backward by up to that jitter — and when the step crosses a
 * second boundary, `ceil((endMs − nowFlooredMs) / 1000)` jumps up by 1, so
 * the countdown visibly hesitates between N and N+1 at the anchor cadence.
 *
 * 100 ms absorbs LAN + reasonable WAN jitter while remaining well below the
 * 1 s display granularity. Real corrections (NTP resync, system clock jump,
 * laptop wake-up) blow past this threshold and re-anchor instantly.
 */
const NTP_OFFSET_HYSTERESIS = 100;

/**
 * Extra time (ms) the synthetic rendu countdown stays active past
 * `T_go + delay`. OTAV's real timing_update (with is_live=true for the new
 * live clip) arrives via WS + REST with some latency — without a buffer,
 * the rendu card briefly empties between synth deactivation and real data
 * arrival. The formula `syntheticEndMs = nextLiveEndMs + duration × 1000`
 * keeps producing the correct value throughout this buffer, so no visual
 * inconsistency occurs.
 */
const SYNTH_HANDOFF_BUFFER_MS = 2_000;

/**
 * Given a locked `endMs` (absolute), the current NTP-aligned wall-clock
 * `now`, and `now` floored to its displayed second, produces the integer
 * countdown value synchronised with the Clock. The ceil ensures the display
 * reads "1" during the last whole displayed second before reaching zero.
 */
function snapSecondsFromEnd(endMs: number, nowFlooredMs: number): number {
  return Math.max(0, Math.ceil((endMs - nowFlooredMs) / 1000));
}

/**
 * State for the stabilisation strategy (kept across renders via useRef).
 * Each locked endMs is keyed by a content identifier so we reset when the
 * underlying clip changes.
 */
interface StableState {
  itemKey: string | null;
  itemEndMs: number | null;
  nextLiveKey: string | null;
  nextLiveEndMs: number | null;
  ntpOffsetMs: number | null;
}

/**
 * Given the latest server anchor and a fixed transmission delay, produces a
 * live-updating InterpolatedTiming snapshot.
 *
 * # Why the hysteresis trick matters
 *
 * The naïve approach of recomputing `endMs = serverNow + remaining × 1000 −
 * delay` on every tick is mathematically constant within a single anchor,
 * but anchors arrive every ~250 ms and each one carries a slightly different
 * `item_remaining_time` and `current_time`. The resulting endMs varies by
 * a few milliseconds between anchors (OTAV precision + network jitter). If
 * that tiny variation happens to straddle a whole-second boundary, the
 * displayed `ceil()` flips between N and N+1 → the countdown visibly
 * "flickers" between two values.
 *
 * The fix: lock `endMs` in a ref and only update it when the new anchor's
 * candidate drifts more than `END_MS_HYSTERESIS` (500 ms) from the stored
 * value, OR when the clip key changes. This gives us perfect stability
 * within a clip while staying reactive to real corrections (NTP resyncs,
 * clip changes, big playout jumps).
 *
 * The same trick is applied a second time to the NTP offset itself
 * (`NTP_OFFSET_HYSTERESIS`) — see that constant for why the displayed
 * wall-clock would otherwise nudge the countdown across a second boundary
 * even though `endMs` is rock-solid.
 *
 * Returns undefined until the first anchor is received.
 */
export function useInterpolatedTiming(
  anchor: TimingAnchor | null,
  transmissionDelayMs: number,
): InterpolatedTiming | undefined {
  const [snapshot, setSnapshot] = useState<InterpolatedTiming | undefined>(undefined);
  const stableRef = useRef<StableState>({
    itemKey: null,
    itemEndMs: null,
    nextLiveKey: null,
    nextLiveEndMs: null,
    ntpOffsetMs: null,
  });

  useEffect(() => {
    if (!anchor) {
      setSnapshot(undefined);
      stableRef.current = {
        itemKey: null,
        itemEndMs: null,
        nextLiveKey: null,
        nextLiveEndMs: null,
        ntpOffsetMs: null,
      };
      return;
    }

    const delaySec = transmissionDelayMs / 1000;

    // ------------------------------------------------------------
    // Stabilise NTP offset (server clock − local clock)
    // ------------------------------------------------------------
    const candidateOffsetMs = anchor.serverNowAtReceipt.getTime() - anchor.receivedAt;
    if (
      stableRef.current.ntpOffsetMs === null ||
      Math.abs(candidateOffsetMs - stableRef.current.ntpOffsetMs) > NTP_OFFSET_HYSTERESIS
    ) {
      stableRef.current.ntpOffsetMs = candidateOffsetMs;
    }

    // ------------------------------------------------------------
    // Stabilise item end time
    // ------------------------------------------------------------
    // We store the *raw* (sub-second) candidate and only round when
    // computing the display. Rounding the candidate BEFORE the hysteresis
    // comparison amplifies jitter: a raw value oscillating ±50 ms around
    // an X.5-second boundary rounds to {X, X+1, X, X+1, ...}, producing
    // 1000 ms steps that systematically defeat the 500 ms threshold and
    // cause permanent flicker (countdown + nextLiveAt both stepping back
    // and forth — see 2026-05-29 telenantes screencast).
    //
    // Storing raw + comparing raw vs raw means jitter <= END_MS_HYSTERESIS
    // never re-anchors. Display alignment is preserved by rounding once
    // at compute time (or relying on snapSecondsFromEnd's ceil semantics).
    const itemKey = anchor.data.item_display_name;
    const candidateItemEndRaw =
      anchor.serverNowAtReceipt.getTime() +
      (anchor.data.item_remaining_time - delaySec) * 1000;
    const prev = stableRef.current;
    if (
      prev.itemKey !== itemKey ||
      prev.itemEndMs === null ||
      Math.abs(candidateItemEndRaw - prev.itemEndMs) > END_MS_HYSTERESIS
    ) {
      stableRef.current.itemKey = itemKey;
      stableRef.current.itemEndMs = candidateItemEndRaw;
    }

    // ------------------------------------------------------------
    // Stabilise next-live end time (if any) — same raw/hysteresis trick
    // ------------------------------------------------------------
    const nextLiveKey = anchor.data.next_live_unique_id ?? anchor.data.next_live_display_name ?? null;
    if (
      nextLiveKey &&
      typeof anchor.data.remaining_time_until_next_live === 'number'
    ) {
      const candidateNextEndRaw =
        anchor.serverNowAtReceipt.getTime() +
        (anchor.data.remaining_time_until_next_live - delaySec) * 1000;
      if (
        stableRef.current.nextLiveKey !== nextLiveKey ||
        stableRef.current.nextLiveEndMs === null ||
        Math.abs(candidateNextEndRaw - stableRef.current.nextLiveEndMs) > END_MS_HYSTERESIS
      ) {
        stableRef.current.nextLiveKey = nextLiveKey;
        stableRef.current.nextLiveEndMs = candidateNextEndRaw;
      }
    } else {
      stableRef.current.nextLiveKey = null;
      stableRef.current.nextLiveEndMs = null;
    }

    const compute = (): InterpolatedTiming => {
      const nowMs = Date.now();
      // Reuse the stabilised offset (vs. recomputing from `anchor` directly)
      // so per-anchor RTT jitter doesn't perturb the displayed wall-clock.
      const offsetMs = stableRef.current.ntpOffsetMs ?? 0;
      const now = new Date(nowMs + offsetMs);
      const nowFlooredMs = now.getTime() - now.getMilliseconds();

      // --- Current item countdown (stable) ---
      // Round the stable RAW end-time at display time so transitions land
      // on the nearest whole second (vs. always rounding up via ceil on a
      // sub-second value). The stored raw is jitter-stable thanks to the
      // raw-vs-raw hysteresis above, so this rounding never re-flickers.
      const itemEndRawMs = stableRef.current.itemEndMs ?? now.getTime();
      const itemEndMs = Math.round(itemEndRawMs / 1000) * 1000;
      const itemRemainingSeconds = snapSecondsFromEnd(itemEndMs, nowFlooredMs);
      const itemRemainingExpired = now.getTime() > itemEndMs;

      // --- Next live countdown (stable) ---
      const nextLiveEndRawMs = stableRef.current.nextLiveEndMs;
      const nextLiveEndMs =
        nextLiveEndRawMs !== null ? Math.round(nextLiveEndRawMs / 1000) * 1000 : null;
      let nextLiveRemainingSeconds: number | undefined;
      let nextLiveAt: Date | undefined;
      let nextLiveCardVisible = false;
      if (nextLiveEndMs !== null) {
        nextLiveRemainingSeconds = snapSecondsFromEnd(nextLiveEndMs, nowFlooredMs);
        nextLiveAt = new Date(nowFlooredMs + nextLiveRemainingSeconds * 1000);
        // Visible until 1 second past T_go (the "zero frame"). After that the
        // card disappears while the synthetic rendu card continues.
        nextLiveCardVisible = nextLiveEndMs - now.getTime() > -1000;
      }

      // --- Synthetic "rendu d'antenne" countdown ---
      // Active during the transmission-delay window between "presenter goes
      // live" (rawNext = 0) and "OTAV confirms the transition" (rawNext
      // = -delay). See full rationale at the top of the file — the condition
      // is based on rawNext only, NOT on is_live, so it covers both the
      // first-live (file → live) and back-to-back (live → live) cases.
      //
      // syntheticEndMs = nextLiveEndMs + duration × 1000 is itself a stable
      // absolute end-time, so no flicker here either.
      let syntheticRenduSeconds: number | undefined;
      let syntheticRenduName: string | undefined;
      const duration = anchor.data.next_live_duration_seconds;
      if (
        typeof duration === 'number' &&
        nextLiveEndMs !== null
      ) {
        const rawNextSec = (nextLiveEndMs - now.getTime()) / 1000;
        // Active from rawNext = 0 (start of the 0-frame, synced with the
        // display transition to "0") all the way to -delay minus the
        // handoff buffer. The buffer absorbs the brief WS/REST latency
        // between OTAV transitioning and real data arriving — avoids a
        // visible "empty card" flash at the synth→real handoff.
        const lowerBound = -delaySec - SYNTH_HANDOFF_BUFFER_MS / 1000;
        if (rawNextSec <= 0 && rawNextSec > lowerBound) {
          const syntheticEndMs = nextLiveEndMs + duration * 1000;
          syntheticRenduSeconds = snapSecondsFromEnd(syntheticEndMs, nowFlooredMs);
          syntheticRenduName = anchor.data.next_live_display_name;
        }
      }

      return {
        now,
        itemRemainingSeconds,
        itemRemainingExpired,
        itemDisplayName: anchor.data.item_display_name,
        isLive: anchor.data.is_live,
        activeSource: anchor.data.active_source,
        nextLiveDisplayName: anchor.data.next_live_display_name,
        nextLiveRemainingSeconds,
        nextLiveAt,
        nextLiveCardVisible,
        syntheticRenduSeconds,
        syntheticRenduName,
        nextLiveDurationSeconds:
          typeof duration === 'number' ? Math.round(duration) : undefined,
        // Anchored on the stabilised, second-snapped `nextLiveEndMs` — never
        // on `nextLiveAt`, which becomes a running clock once the countdown
        // clamps to 0. No new stabilisation needed: the ISO path is absolute
        // and the offset only moves when the collector re-walks an edited
        // playlist (at most once a minute) — a real change, not jitter. So
        // this inherits exactly the jitter protection of fe67941/40f7c75.
        followingLive: resolveFollowingLive(anchor.data, nextLiveEndMs, transmissionDelayMs),
      };
    };

    // Recompute immediately whenever the anchor changes, then keep ticking.
    setSnapshot(compute());
    const id = window.setInterval(() => setSnapshot(compute()), INTERP_TICK_MS);
    return () => window.clearInterval(id);
  }, [anchor, transmissionDelayMs]);

  return snapshot;
}

/** Helper to build an anchor from a TimingData payload received "now". */
export function makeAnchor(data: TimingData): TimingAnchor {
  const receivedAt = Date.now();
  const serverNowAtReceipt = parseServerTimeOfDay(data.current_time, new Date(receivedAt));
  return { data, receivedAt, serverNowAtReceipt };
}
